/**
 * <mobli-wa-quote> — the WhatsApp pre-quote.
 *
 * One short form, two ways out. The send control is a real anchor whose href is
 * rebuilt on every keystroke, so the message leaves with the selection already
 * written into it — the click never depends on a handler winning a race. The
 * same fields are posted to Shopify's contact form in the background, so the
 * lead survives a shopper who opens WhatsApp and never presses send.
 *
 * The pieces come from `[data-waq-lines]` when the page can render them in
 * Liquid (the cart), or from `setSummary()` when a live selection owns them
 * (the product page, the space builder).
 */
class MobliWaQuote extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-waq-dialog]');
    // Liquid's `{% form %}` tag owns the element's attributes, so it is found
    // by tag name rather than a data hook.
    this.form = this.querySelector('form');
    this.link = this.querySelector('[data-waq-send]');
    this.preview = this.querySelector('[data-waq-preview]');
    this.linesEl = this.querySelector('[data-waq-lines]');
    this.totalEl = this.querySelector('[data-waq-total]');

    this.addEventListener('click', this.#onClick);
    this.addEventListener('input', this.#compose);
    this.addEventListener('change', this.#compose);

    this.#compose();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('input', this.#compose);
    this.removeEventListener('change', this.#compose);
  }

  /**
   * Hand the element a freshly composed selection. Hosts with live state — the
   * PDP's variant picker, the space builder's canvas — call this on every
   * render so the message never lags behind what the shopper is looking at.
   *
   * @param {string} lines - one piece per line.
   * @param {string} [total] - the estimate, already formatted as money.
   */
  setSummary(lines, total) {
    this.summary = this.#clean(lines);
    if (total != null) this.totalText = total;
    this.#compose();
  }

  /* ---------- interaction ---------- */

  #onClick = (event) => {
    if (event.target.closest('[data-waq-open]')) return this.#open();
    if (event.target.closest('[data-waq-close]')) return this.#close();

    const send = event.target.closest('[data-waq-send]');
    if (send) this.#send(event);
  };

  #open() {
    this.#compose();
    if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
    else this.dialog.setAttribute('open', '');
  }

  #close() {
    if (typeof this.dialog.close === 'function') this.dialog.close();
    else this.dialog.removeAttribute('open');
  }

  #send = (event) => {
    if (this.form && !this.form.checkValidity()) {
      event.preventDefault();
      this.form.reportValidity();
      return;
    }

    // Rebuild in the click itself: the anchor's default action runs after this
    // handler, so the href set here is the one the browser follows.
    this.#compose();
    this.#record();

    const done = this.querySelector('[data-waq-done]');
    if (done) done.hidden = false;
  };

  /* ---------- the message ---------- */

  #clean(text) {
    return (text || '')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .join('\n');
  }

  #field(name) {
    if (!this.form) return '';
    const input = this.form.querySelector(`[name="contact[${name}]"]`);
    return input && input.value ? input.value.trim() : '';
  }

  /** The address the shopper actually typed, never the phone-derived stand-in. */
  #typedEmail() {
    const visible = this.form && this.form.querySelector('[data-waq-email]');
    return visible && visible.value ? visible.value.trim() : '';
  }

  #lines() {
    if (this.summary != null) return this.summary;
    return this.#clean(this.linesEl ? this.linesEl.textContent : '');
  }

  #total() {
    if (this.totalText != null) return this.totalText;
    return this.totalEl ? this.totalEl.textContent.trim() : '';
  }

  #message() {
    const parts = [this.dataset.intro || 'Hola, quiero una precotización.'];

    const lines = this.#lines();
    if (lines) parts.push(`*${this.dataset.piecesLabel || 'Mi selección'}*\n${lines}`);

    const total = this.#total();
    if (total) parts.push(`*Estimado:* ${total}`);

    const details = [
      ['Nombre', this.#field('name')],
      ['Celular', this.#field('phone')],
      ['Correo', this.#typedEmail()],
      ['Ciudad', this.#field('Ciudad')],
      ['Entrega estimada', this.#field('Fecha estimada')],
    ]
      .filter(([, value]) => value)
      .map(([label, value]) => `${label}: ${value}`);

    if (details.length) parts.push(`*Mis datos*\n${details.join('\n')}`);
    if (this.dataset.page) parts.push(this.dataset.page);

    return parts.join('\n\n');
  }

  #compose = () => {
    const text = this.#message();

    if (this.preview) this.preview.textContent = text;
    if (this.link && this.dataset.phone) {
      this.link.href = `https://wa.me/${this.dataset.phone}?text=${encodeURIComponent(text)}`;
    }

    // The advisor reads the whole message in the Shopify notification too, so
    // the two channels never tell a different story.
    const resumen = this.querySelector('[data-waq-resumen]');
    if (resumen) resumen.value = text;

    this.#syncEmail();
  };

  /**
   * Shopify rejects a contact submission without a syntactically valid email,
   * and asking for one is the field shoppers abandon on. So the visible field
   * stays optional and, when it is blank, the phone stands in as an address on
   * the reserved `.invalid` TLD — the submission registers, and the advisor
   * reads the real number in the body and replies where the shopper is.
   */
  #syncEmail() {
    if (!this.form) return;

    const visible = this.form.querySelector('[data-waq-email]');
    const carrier = this.form.querySelector('[data-waq-email-carrier]');
    if (!visible || !carrier) return;

    const typed = this.#typedEmail();
    if (typed) {
      carrier.value = typed;
      return;
    }

    const digits = this.#field('phone').replace(/\D/g, '');
    carrier.value = digits ? `${digits}@whatsapp.invalid` : 'sin-correo@whatsapp.invalid';
  }

  /* ---------- the lead ---------- */

  #record() {
    if (!this.form || this.recorded) return;
    this.recorded = true;

    try {
      fetch(this.form.action, {
        method: 'POST',
        body: new FormData(this.form),
        // The tab is about to hand off to WhatsApp; keep the request alive.
        keepalive: true,
      }).catch(() => {});
    } catch {
      // The message is already on its way. Losing the copy in Shopify is worth
      // less than blocking the shopper, so this failure stays silent.
    }
  }
}

if (!customElements.get('mobli-wa-quote')) {
  customElements.define('mobli-wa-quote', MobliWaQuote);
}
